//@ts-strict-ignore
import React, { forwardRef, ReactNode, useRef } from 'react';
import classNames from 'classnames';

type IOptionProps = {
  children: ReactNode;
  className: string; // className (based on mouse position)
  instancePrefix: string; // unique prefix for the ids (used for aria)
  isDisabled: boolean; // the option is disabled
  isFocused: boolean; // the option is focused
  isSelected: boolean; // the option is selected
  onFocus: Function; // method to handle mouseEnter on option element
  onSelect: Function; // method to handle click on option element
  onUnfocus: Function; // method to handle mouseLeave on option element
  option: any; // object that is base for that option
  optionIndex: number; // index of the option, used to generate unique ids for aria
  testid?: string;
};

const Option = forwardRef<HTMLDivElement, IOptionProps>(
  ({ option, testid, onFocus, className, onSelect, isFocused, children, instancePrefix, optionIndex }, ref) => {
    const dragging = useRef(false);

    const blockEvent = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (event.target.tagName !== 'A' || !('href' in event.target)) {
        return;
      }
      if (event.target.target) {
        window.open(event.target.href, event.target.target);
      } else {
        window.location.href = event.target.href;
      }
    };

    const handleMouseDown = (event) => {
      event.preventDefault();
      event.stopPropagation();
      onSelect(option, event);
    };

    const handleMouseEnter = (event) => {
      handleFocus(event);
    };

    const handleMouseMove = (event) => {
      handleFocus(event);
    };

    const handleTouchEnd = (event) => {
      // Check if the view is being dragged, In this case
      // we don't want to fire the click event (because the user only wants to scroll)
      if (dragging.current) return;

      handleMouseDown(event);
    };

    const handleTouchMove = (event) => {
      // Set a flag that the view is being dragged
      dragging.current = true;
    };

    const handleTouchStart = (event) => {
      // Set a flag that the view is not being dragged
      dragging.current = false;
    };

    const handleFocus = (event) => {
      if (!isFocused) {
        onFocus(option, event);
      }
    };

    const divClassName = classNames(className, option.className);

    return option.disabled ? (
      <div ref={ref} className={divClassName} onMouseDown={blockEvent} onClick={blockEvent}>
        {children}
      </div>
    ) : (
      <div
        ref={ref}
        className={divClassName}
        style={option.style}
        role="option"
        onMouseDown={handleMouseDown}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        id={`${instancePrefix}-option-${optionIndex}`}
        title={option.title}
        data-testid={testid}
      >
        {children}
      </div>
    );
  }
);

export default Option;
